import { apiClient } from '../apiClient';

import type { AssessmentRubric } from './rubric';

import type { TopicVisualization } from '@/features/Visualizer';

// --- Curriculum overview ---

export interface TopicWithStatus {
  id: string;
  title: string;
  /** One-line "why this topic" shown under the title in curriculum lists. */
  subtitle?: string;
  /** Technique tag (e.g. "Two Pointers") driving the "By pattern" lens. */
  pattern?: string;
  sort_order: number;
  estimated_hours?: number;
  topic_type: string;
  status: 'not_started' | 'in_progress' | 'completed';
  /**
   * Optional rather than required: during a rolling deploy the client can be
   * newer than the API, and a missing flag should read as "no animation"
   * instead of breaking the curriculum list.
   */
  has_visualization?: boolean;
}

export interface ModuleWithProgress {
  id: string;
  title: string;
  description?: string;
  sort_order: number;
  phase: string;
  topic_count: number;
  completed_count: number;
  topics: TopicWithStatus[];
}

export interface CurriculumOverview {
  batch_id: string;
  batch_name: string;
  trainer_name: string;
  curriculum_id: string;
  template_title: string;
  total_topics: number;
  completed_topics: number;
  modules: ModuleWithProgress[];
}

// --- Topic detail ---

export interface Session {
  id: string;
  title: string;
  scheduled_at?: string;
  meeting_url?: string;
  recording_url?: string;
  duration_mins?: number;
  trainer_name?: string;
}

export interface Resource {
  id: string;
  title: string;
  type: string;
  url: string;
}

export interface Assessment {
  id: string;
  title: string;
  description?: string;
  type: string;
  max_score?: number;
  due_date?: string;
  difficulty?: string;
  asked_in?: string[];
  submitted?: boolean;
  solved?: boolean;
  problem_slug?: string;
  assignment_kind?: string;
}

export interface Note {
  id: string;
  content: string;
  created_at: string;
  updated_at: string;
}

export interface TopicFeedback {
  id: string;
  rating: number;
  comment: string;
  created_at: string;
  updated_at: string;
}

export interface TopicDetail {
  id: string;
  title: string;
  description?: string;
  subtitle?: string;
  pattern?: string;
  complexity_time?: string;
  complexity_space?: string;
  /** Pre-authored step-script animation; absent for topics not yet authored. */
  visualization?: TopicVisualization | null;
  sort_order: number;
  estimated_hours?: number;
  topic_type: string;
  module_title: string;
  sessions: Session[];
  resources: Resource[];
  assessments: Assessment[];
  notes: Note[];
  my_feedback?: TopicFeedback | null;
}

// --- Leaderboard ---

// The leaderboard carries no per-entry breakdown. It used to ship a v1-shaped
// `components` object next to a v2 `readiness_score`, which no longer added up
// to the score beside it; the API dropped the field. The student's own
// breakdown comes from /lms/home (`readiness.breakdown`), which is versioned
// with the score it explains.
export interface LeaderboardEntry {
  rank: number;
  user_id: string;
  username: string;
  avatar_url: string | null;
  readiness_score: number;
  tier: 'A' | 'B' | 'C' | 'needs_support';
  streak_days: number;
  is_caller: boolean;
}

export interface BatchStats {
  id: string;
  name: string;
  total_students: number;
  median_score: number;
}

export interface BatchLeaderboard {
  batch: BatchStats;
  scope_label: string;
  caller_rank: number;
  caller_percentile: number;
  points_to_next_rank: number;
  top: LeaderboardEntry[];
  neighbourhood: LeaderboardEntry[];
}

// --- Upcoming ---

export interface UpcomingItem {
  id: string;
  title: string;
  type: string;            // 'live_class' | 'lab' | 'office_hours' | 'recorded'
  scheduled_at?: string;   // ISO datetime; absent for assessments/assignments with no due date set
  topic_id: string;
  topic_title: string;
  module_title: string;
  meeting_url?: string;
  // Lets "All Courses" mode (batchId 'all') link each item back to the batch
  // it belongs to, since a single request can span several batches.
  batch_id: string;
}

// --- Schedule (calendar) ---

export type ScheduleKind = 'session' | 'assessment' | 'hackathon' | 'contest';
/**
 * `attended` is a session the register marked present/late. `done` is a class
 * that was held with no attendance record, or a submitted assessment — the API
 * won't claim you skipped a class nobody took a register for.
 */
export type ScheduleState = 'upcoming' | 'live' | 'attended' | 'done' | 'missed';

/**
 * One dated thing on the calendar. Unlike UpcomingItem this covers the past
 * too, so a month you've already lived through isn't empty.
 */
export interface ScheduleItem {
  id: string;
  kind: ScheduleKind;
  /** problems | quizzes | assignments | projects — assessments only */
  category?: string;
  title: string;
  at: string; // ISO datetime
  batch_id?: string;
  batch_name?: string;
  topic_id?: string;
  topic_title?: string;
  url?: string;
  recording_url?: string;
  state: ScheduleState;
}

// --- Session Recording ---

export interface SessionRecording {
  id: string;
  title: string;
  session_type: string;
  scheduled_at: string;
  duration_mins?: number;
  trainer_name?: string;
  recording_url: string;
  topic_id: string;
  topic_title: string;
}

// --- My batches ---

export interface MyBatch {
  id: string;
  name: string;
  trainer_name: string;
  total_topics: number;
  completed_topics: number;
  progress_pct: number;
}

// --- Practice ---

/**
 * Exhaustive on purpose. Every Record and switch keyed by this union is
 * checked against it, so adding a member makes the compiler name each place
 * that has to decide what the new category looks like — which is the only
 * defence against the server's own fallback, where an unrecognised type is
 * filed under Assignments in silence.
 */
export type PracticeCategoryKey = 'problems' | 'quizzes' | 'assignments' | 'projects' | 'viva';

export interface PracticeCategory {
  key: PracticeCategoryKey;
  label: string;
  total: number;
  completed: number;
}

export interface PracticeItem {
  id: string;
  title: string;
  category: PracticeCategoryKey;
  type: string; // mcq_quiz | coding_challenge | project_assignment
  topic_id: string;
  topic_title: string;
  module_title: string;
  difficulty?: string;
  max_score?: number;
  /** Effective due date: explicit, or derived from due_relative_days + topic start. */
  due_date?: string;
  problem_slug?: string;
  /** Set membership. Absent on the 2525 legacy assessments, which stay loose. */
  group_key?: string;
  group_label?: string;
  /**
   * Step of the weekly ladder — the 3 warm-up / 4 core / 2 stretch / 1
   * debug-this shape every set is authored to. Absent on anything not on a
   * ladder (legacy assessments, quizzes, assignments, vivas).
   */
  rung?: PracticeRung;
  submitted: boolean;
  score?: number;
}

export type PracticeRung = 'warm_up' | 'core' | 'stretch' | 'debug';

/**
 * One authored set (a week's ten problems, say) collapsed to a single card.
 * Derived server-side from `items`; every member is still present in `items`,
 * so groups are additive and never hide work.
 */
export interface PracticeGroup {
  key: string;
  label: string;
  total: number;
  completed: number;
  due_date?: string;
}

export interface PracticeOverview {
  batch_id: string;
  batch_name: string;
  total_items: number;
  completed_items: number;
  categories: PracticeCategory[];
  groups: PracticeGroup[];
  pending: PracticeItem[];
  items: PracticeItem[];
}

export interface PracticeAssessmentDetail {
  id: string;
  title: string;
  description?: string;
  category: PracticeCategoryKey;
  type: string;
  difficulty?: string;
  max_score?: number;
  due_date?: string;
  problem_slug?: string;
  asked_in?: string[];
  batch_id: string;
  topic_id: string;
  topic_title: string;
  module_title: string;
  created_by_name?: string;
  resources: Resource[];
  submitted: boolean;
  submission_url?: string;
  score?: number;
  feedback?: string;
  submitted_at?: string;
  /** The marking scheme, shown read-only to the student before they submit. */
  rubric?: AssessmentRubric;
  /** The caller's own per-criterion marks, once a grader has entered them. */
  rubric_scores?: Record<string, number>;
}

// --- Response types ---

export interface StatusResponse {
  status: string;
}

// --- API methods ---

export const lmsApi = {
  listMyBatches: () =>
    apiClient.get<MyBatch[]>('/api/v1/lms/batches'),

  getBatchCurriculum: (batchId: string) =>
    apiClient.get<CurriculumOverview>(`/api/v1/lms/batches/${batchId}/curriculum`),

  getTopicDetail: (topicId: string) =>
    apiClient.get<TopicDetail>(`/api/v1/lms/topics/${topicId}`),

  completeTopic: (topicId: string) =>
    apiClient.post<StatusResponse>(`/api/v1/lms/topics/${topicId}/complete`),

  uncompleteTopic: (topicId: string) =>
    apiClient.delete<StatusResponse>(`/api/v1/lms/topics/${topicId}/complete`),

  createNote: (topicId: string, content: string) =>
    apiClient.post<Note>(`/api/v1/lms/topics/${topicId}/notes`, { content }),

  submitFeedback: (topicId: string, rating: number, comment: string) =>
    apiClient.post<TopicFeedback>(`/api/v1/lms/topics/${topicId}/feedback`, { rating, comment }),

  getLeaderboard: (batchId: string) =>
    apiClient.get<BatchLeaderboard>(`/api/v1/lms/batches/${batchId}/leaderboard`),
  getCourseLeaderboard: (batchId: string) =>
    apiClient.get<BatchLeaderboard>(`/api/v1/lms/batches/${batchId}/leaderboard/course`),

  getUpcoming: (batchId: string) =>
    apiClient.get<UpcomingItem[]>(`/api/v1/lms/batches/${batchId}/upcoming`),

  getSchedule: (batchId: string, from: string, to: string) =>
    apiClient.get<ScheduleItem[]>('/api/v1/lms/schedule', {
      params: { batch_id: batchId, from, to },
    }),

  getRecordings: (batchId: string) =>
    apiClient.get<SessionRecording[]>(`/api/v1/lms/batches/${batchId}/sessions/recordings`),

  getPractice: (batchId: string) =>
    apiClient.get<PracticeOverview>(`/api/v1/lms/batches/${batchId}/practice`),

  getPracticeAssessment: (assessmentId: string) =>
    apiClient.get<PracticeAssessmentDetail>(`/api/v1/lms/practice/${assessmentId}`),
};
